import { useState, useEffect, useCallback } from 'react';
import { wpApi, Speaker, SpeakerMetadata, PaginationParams, PaginatedResponse, utils, SpeakersQueryParams, FilterParams } from '../services/wordpressApi';

// Type definitions for hook return values
interface UseSpeakersReturn {
  speakers: Speaker[];
  loading: boolean;
  error: string | null;
  totalPages: number;
  totalItems: number;
  currentPage: number;
  perPage: number;
}

// Interface for paginated speakers hook
interface UsePaginatedSpeakersReturn extends UseSpeakersReturn {
  goToPage: (page: number) => void;
  nextPage: () => void;
  prevPage: () => void;
  hasNextPage: boolean;
  hasPrevPage: boolean;
}

// New interface for server-side filtered speakers hook
interface UseFilteredSpeakersReturn extends UseSpeakersReturn {
  goToPage: (page: number) => void;
  updateFilters: (filters: FilterParams) => void;
  hasNextPage: boolean;
  hasPrevPage: boolean;
  filters: FilterParams;
}

// Interface for speakers metadata hook (unchanged)
interface UseSpeakersMetadataReturn {
  speakersMetadata: SpeakerMetadata[];
  loading: boolean;
  error: string | null;
}

interface UseApiTestReturn {
  connectionStatus: 'untested' | 'testing' | 'success' | 'failed';
  loading: boolean;
  testConnection: () => Promise<void>;
}

interface UseHomepageSpeakersReturn {
  homepageSpeakers: Speaker[];
  currentSpeaker: Speaker | null;
  currentSpeakerIndex: number;
  loading: boolean;
  error: string | null;
}

interface UseHomepageSpeakersOptions {
  startRotationDelay?: number;
}

interface UseSpeakerReturn {
  speaker: Speaker | null;
  loading: boolean;
  error: string | null;
}

const SPEAKERS_CACHE_KEY = 'ck_speakers_cache_v1';
const SPEAKERS_METADATA_CACHE_KEY = 'ck_speakers_metadata_cache_v1';

// Static homepage speakers (local images, used only on homepage)
const STATIC_HOMEPAGE_SPEAKERS: Speaker[] = [
  {
		id: 2,
		title: { rendered: 'Cynt Marshall' },
		content: { rendered: '' },
		excerpt: { rendered: '' },
		featured_media: 0,
		featured_image_url: '/homepage-speakers/cynt-marshall.jpeg',
		acf: {
			speaker_type_featured_on_homepage_or_standard: 'homepage_speaker',
			biography: '',
			company: 'Dallas Mavericks',
			topic: '',
			speaker_image: {
				url: '/homepage-speakers/cynt-marshall.jpeg',
				alt: 'Cynt Marshall',
			},
		},
	},
	{
		id: 3,
		title: { rendered: 'Danny Meyer' },
		content: { rendered: '' },
		excerpt: { rendered: '' },
		featured_media: 0,
		featured_image_url: '/homepage-speakers/danny-meyer.jpeg',
		acf: {
			speaker_type_featured_on_homepage_or_standard: 'homepage_speaker',
			biography: '',
			company: 'Shake Shack',
			topic: '',
			speaker_image: {
				url: '/homepage-speakers/danny-meyer.jpeg',
				alt: 'Danny Meyer',
			},
		},
	},

	{
		id: 1,
		title: { rendered: 'Marc Randolph' },
		content: { rendered: '' },
		excerpt: { rendered: '' },
		featured_media: 0,
		featured_image_url: '/homepage-speakers/marc-randolph.jpeg',
		acf: {
			speaker_type_featured_on_homepage_or_standard: 'homepage_speaker',
			biography: '',
			company: 'Netflix',
			topic: '',
			speaker_image: {
				url: '/homepage-speakers/marc-randolph.jpeg',
				alt: 'Marc Randolph',
			},
		},
	},
];

// Hook for fetching lightweight speaker metadata for search/filtering
export const useSpeakersMetadata = (): UseSpeakersMetadataReturn => {
  const [speakersMetadata, setSpeakersMetadata] = useState<SpeakerMetadata[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchSpeakersMetadata = async (): Promise<void> => {
      try {
        // Try session cache first
        const cached = sessionStorage.getItem(SPEAKERS_METADATA_CACHE_KEY);
        if (cached) {
          const parsed: SpeakerMetadata[] = JSON.parse(cached);
          setSpeakersMetadata(parsed);
          setLoading(false);
          // Background refresh without flipping to loading
          wpApi.getSpeakersMetadata().then((data) => {
            setSpeakersMetadata(data);
            sessionStorage.setItem(SPEAKERS_METADATA_CACHE_KEY, JSON.stringify(data));
          }).catch(() => {/* ignore background errors */});
          return;
        }

        setLoading(true);
        setError(null);
        
        const data = await wpApi.getSpeakersMetadata();
        setSpeakersMetadata(data);
        // Cache for back navigation
        sessionStorage.setItem(SPEAKERS_METADATA_CACHE_KEY, JSON.stringify(data));
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    fetchSpeakersMetadata();
  }, []);

  return { speakersMetadata, loading, error };
};

// Hook for paginated speakers
export const usePaginatedSpeakers = (initialPage: number = 1, perPage: number = 10): UsePaginatedSpeakersReturn => {
  const [speakers, setSpeakers] = useState<Speaker[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(initialPage);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [totalItems, setTotalItems] = useState<number>(0);

  const fetchSpeakers = async (page: number): Promise<void> => {
    try {
      setLoading(true);
      setError(null);
      
      const result = await wpApi.getSpeakers({ page, perPage });
      
      // Clean up guid and link fields
      const cleanedData = result.data.map(speaker => {
        if (speaker.guid && speaker.guid.rendered) {
          speaker.guid.rendered = utils.cleanSourceDomain(speaker.guid.rendered);
        }
        if (speaker.link) {
          speaker.link = utils.cleanSourceDomain(speaker.link);
        }
        return speaker;
      });
      
      setSpeakers(cleanedData);
      setTotalPages(result.totalPages);
      setTotalItems(result.totalItems);
      setCurrentPage(result.currentPage);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSpeakers(currentPage);
  }, [currentPage, perPage]);

  const goToPage = (page: number): void => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const nextPage = (): void => {
    if (currentPage < totalPages) {
      setCurrentPage(prev => prev + 1);
    }
  };

  const prevPage = (): void => {
    if (currentPage > 1) {
      setCurrentPage(prev => prev - 1);
    }
  };

  const hasNextPage = currentPage < totalPages;
  const hasPrevPage = currentPage > 1;

  return {
    speakers,
    loading,
    error,
    totalPages,
    totalItems,
    currentPage,
    perPage,
    goToPage,
    nextPage,
    prevPage,
    hasNextPage,
    hasPrevPage
  };
};

// Legacy hook for backward compatibility - now returns paginated data for page 1
export const useSpeakers = (): UseSpeakersReturn => {
  const [speakers, setSpeakers] = useState<Speaker[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [perPage, setPerPage] = useState<number>(10);

  useEffect(() => {
    const fetchSpeakers = async (): Promise<void> => {
      try {
        // Try session cache first
        const cached = sessionStorage.getItem(SPEAKERS_CACHE_KEY);
        if (cached) {
          const parsed: PaginatedResponse<Speaker> = JSON.parse(cached);
          setSpeakers(parsed.data);
          setTotalPages(parsed.totalPages);
          setTotalItems(parsed.totalItems);
          setCurrentPage(parsed.currentPage);
          setPerPage(parsed.perPage);
          setLoading(false);
          // Background refresh without flipping to loading
          wpApi.getSpeakers({ page: 1, perPage: 10 }).then((data) => {
            // Clean up guid and link fields
            const cleanedData = data.data.map(speaker => {
              if (speaker.guid && speaker.guid.rendered) {
                speaker.guid.rendered = utils.cleanSourceDomain(speaker.guid.rendered);
              }
              if (speaker.link) {
                speaker.link = utils.cleanSourceDomain(speaker.link);
              }
              return speaker;
            });
            
            setSpeakers(cleanedData);
            setTotalPages(data.totalPages);
            setTotalItems(data.totalItems);
            sessionStorage.setItem(SPEAKERS_CACHE_KEY, JSON.stringify({...data, data: cleanedData}));
          }).catch(() => {/* ignore background errors */});
          return;
        }

        setLoading(true);
        setError(null);
        
        const result = await wpApi.getSpeakers({ page: 1, perPage: 10 });
        
        // Clean up guid and link fields
        const cleanedData = result.data.map(speaker => {
          if (speaker.guid && speaker.guid.rendered) {
            speaker.guid.rendered = utils.cleanSourceDomain(speaker.guid.rendered);
          }
          if (speaker.link) {
            speaker.link = utils.cleanSourceDomain(speaker.link);
          }
          return speaker;
        });
        
        setSpeakers(cleanedData);
        setTotalPages(result.totalPages);
        setTotalItems(result.totalItems);
        setCurrentPage(result.currentPage);
        setPerPage(result.perPage);
        // Cache for back navigation
        sessionStorage.setItem(SPEAKERS_CACHE_KEY, JSON.stringify({...result, data: cleanedData}));
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    fetchSpeakers();
  }, []);

  return { 
    speakers, 
    loading, 
    error, 
    totalPages, 
    totalItems, 
    currentPage, 
    perPage 
  };
};

// Hook for testing API connection
export const useApiTest = (): UseApiTestReturn => {
  const [connectionStatus, setConnectionStatus] = useState<'untested' | 'testing' | 'success' | 'failed'>('untested');
  const [loading, setLoading] = useState<boolean>(false);

  const testConnection = async (): Promise<void> => {
    try {
      setLoading(true);
      setConnectionStatus('testing');
      
      await wpApi.testConnection();
      setConnectionStatus('success');
    } catch (error) {
      setConnectionStatus('failed');
    } finally {
      setLoading(false);
    }
  };

  return { connectionStatus, loading, testConnection };
};

// Hook for fetching homepage speakers from WordPress (used on Speakers page)
export const useHomepageSpeakers = (options: UseHomepageSpeakersOptions = {}): UseHomepageSpeakersReturn => {
  const { startRotationDelay = 0 } = options;
  const [homepageSpeakers, setHomepageSpeakers] = useState<Speaker[]>([]);
  const [currentSpeakerIndex, setCurrentSpeakerIndex] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [canStartRotation, setCanStartRotation] = useState<boolean>(false);

  // Fetch homepage speakers on mount
  useEffect(() => {
    const fetchHomepageSpeakers = async (): Promise<void> => {
      try {
        setLoading(true);
        setError(null);
        
        const data = await wpApi.getHomepageSpeakers();
        
        // Clean up guid and link fields
        const cleanedData = data.map(speaker => {
          if (speaker.guid && speaker.guid.rendered) {
            speaker.guid.rendered = utils.cleanSourceDomain(speaker.guid.rendered);
          }
          if (speaker.link) {
            speaker.link = utils.cleanSourceDomain(speaker.link);
          }
          return speaker;
        });
        
        // WordPress API already returns enabled + sorted homepage speakers
        setHomepageSpeakers(cleanedData);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    fetchHomepageSpeakers();
  }, []);

  // Start rotation after specified delay
  useEffect(() => {
    if (!loading && homepageSpeakers.length > 0) {
      const timer = setTimeout(() => {
        setCanStartRotation(true);
      }, startRotationDelay);

      return () => clearTimeout(timer);
    }
    return undefined;
  }, [loading, homepageSpeakers.length, startRotationDelay]);

  // Rotate speakers every 7 seconds, but only after we're allowed to start
  useEffect(() => {
    if (canStartRotation && homepageSpeakers.length > 1) {
      const interval = setInterval(() => {
        setCurrentSpeakerIndex((prevIndex: number) => 
          prevIndex === homepageSpeakers.length - 1 ? 0 : prevIndex + 1
        );
      }, 4500);

      return () => clearInterval(interval);
    }
    return undefined;
  }, [canStartRotation, homepageSpeakers.length]);

  const currentSpeaker: Speaker | null = homepageSpeakers[currentSpeakerIndex] || null;

  return { 
    homepageSpeakers, 
    currentSpeaker, 
    currentSpeakerIndex,
    loading, 
    error 
  };
};

// Hook for static homepage speakers (used only on Home page Hero)
export const useStaticHomepageSpeakers = (options: UseHomepageSpeakersOptions = {}): UseHomepageSpeakersReturn => {
  const { startRotationDelay = 0 } = options;
  const [homepageSpeakers, setHomepageSpeakers] = useState<Speaker[]>([]);
  const [currentSpeakerIndex, setCurrentSpeakerIndex] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [canStartRotation, setCanStartRotation] = useState<boolean>(false);

  // Load static homepage speakers on mount (simulate previous loading time)
  useEffect(() => {
    const loadStaticHomepageSpeakers = async (): Promise<void> => {
      try {
        setLoading(true);
        setError(null);
        
        // Simulate network delay to retain perceived loading time
        await new Promise((resolve) => setTimeout(resolve, 1000));

        setHomepageSpeakers(STATIC_HOMEPAGE_SPEAKERS);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    loadStaticHomepageSpeakers();
  }, []);

  // Start rotation after specified delay
  useEffect(() => {
    if (!loading && homepageSpeakers.length > 0) {
      const timer = setTimeout(() => {
        setCanStartRotation(true);
      }, startRotationDelay);

      return () => clearTimeout(timer);
    }
    return undefined;
  }, [loading, homepageSpeakers.length, startRotationDelay]);

  // Rotate speakers every 6 seconds, but only after we're allowed to start
  useEffect(() => {
    if (canStartRotation && homepageSpeakers.length > 1) {
      const interval = setInterval(() => {
        setCurrentSpeakerIndex((prevIndex: number) => 
          prevIndex === homepageSpeakers.length - 1 ? 0 : prevIndex + 1
        );
      }, 5500);

      return () => clearInterval(interval);
    }
    return undefined;
  }, [canStartRotation, homepageSpeakers.length]);

  const currentSpeaker: Speaker | null = homepageSpeakers[currentSpeakerIndex] || null;

  return { 
    homepageSpeakers, 
    currentSpeaker, 
    currentSpeakerIndex,
    loading, 
    error 
  };
};

// Hook for server-side filtered speakers (for Browse component)
export const useFilteredSpeakers = (
  initialPage: number = 1, 
  perPage: number = 10
): UseFilteredSpeakersReturn => {
  const [speakers, setSpeakers] = useState<Speaker[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(initialPage);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [filters, setFilters] = useState<FilterParams>({});

  const fetchSpeakers = async (page: number, filterParams: FilterParams): Promise<void> => {
    try {
      setLoading(true);
      setError(null);
      
      const queryParams: SpeakersQueryParams = {
        page,
        perPage,
        ...filterParams
      };
      
      // console.log('🔍 [DEBUG] useFilteredSpeakers fetching:', queryParams);
      
      const result = await wpApi.getSpeakers(queryParams);
      
      // Clean up guid and link fields
      const cleanedData = result.data.map(speaker => {
        if (speaker.guid && speaker.guid.rendered) {
          speaker.guid.rendered = utils.cleanSourceDomain(speaker.guid.rendered);
        }
        if (speaker.link) {
          speaker.link = utils.cleanSourceDomain(speaker.link);
        }
        return speaker;
      });
      
      setSpeakers(cleanedData);
      setTotalPages(result.totalPages);
      setTotalItems(result.totalItems);
      setCurrentPage(result.currentPage);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Fetch speakers when page or filters change
  useEffect(() => {
    fetchSpeakers(currentPage, filters);
  }, [currentPage, filters, perPage]); // Removed perPage from dependency array to avoid infinite loop if perPage changes

  const goToPage = (page: number): void => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const updateFilters = useCallback((newFilters: FilterParams): void => {
    setFilters(newFilters);
    setCurrentPage(1); // Reset to first page when filters change
  }, []);

  const hasNextPage = currentPage < totalPages;
  const hasPrevPage = currentPage > 1;

  return {
    speakers,
    loading,
    error,
    totalPages,
    totalItems,
    currentPage,
    perPage,
    goToPage,
    updateFilters,
    hasNextPage,
    hasPrevPage,
    filters
  };
};


// Hook to fetch a single speaker by ID
export const useSpeaker = (identifier: number | string | undefined): UseSpeakerReturn => {
  const [speaker, setSpeaker] = useState<Speaker | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchSpeaker = async (): Promise<void> => {
      try {
        if (identifier === undefined || identifier === null) {
          throw new Error('Speaker ID is required');
        }
        setLoading(true);
        setError(null);
        let data: Speaker;
        // If identifier looks like a number, treat as id; otherwise slug
        const isNumeric = typeof identifier === 'number' || (typeof identifier === 'string' && /^\d+$/.test(identifier));
        if (isNumeric) {
          data = await wpApi.getSpeakerById(identifier);
        } else {
          data = await wpApi.getSpeakerBySlug(String(identifier));
        }
        
        // Clean up guid and link fields to remove ravespeakers.com
        if (data.guid && data.guid.rendered) {
          data.guid.rendered = utils.cleanSourceDomain(data.guid.rendered);
        }
        if (data.link) {
          data.link = utils.cleanSourceDomain(data.link);
        }
        
        setSpeaker(data);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    fetchSpeaker();
  }, [identifier]);

  return { speaker, loading, error };
};